import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { http, toastApiError } from "../lib/api";
import { formKeyNavWith } from "../lib/formNav";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { readJson, writeJson } from "../lib/storage";
import { safePath } from "../lib/nav";
import { toast } from "../lib/notify";
import { Save, X, Lock } from "lucide-react";
import PersonnelExportDialog from "../components/PersonnelExportDialog";
import BulkRaiseDialog from "../components/personnel/BulkRaiseDialog";
import PersonnelWage, { DEFAULT_WAGE_TYPES } from "../components/personnel/PersonnelWage";
import PersonnelBulkUpload from "../components/personnel/PersonnelBulkUpload";
import WageChangeReportDialog from "../components/personnel/WageChangeReportDialog";
import SgkLinks from "../components/SgkLinks";
import TempAssignmentHistory from "../components/TempAssignmentHistory";
import PersonnelDocuments from "../components/PersonnelDocuments";
import { useAuth } from "../context/AuthContext";
import { canSgk } from "../lib/roles";
import { useColFilters } from "../components/ColFilters";
import PersonnelPayroll from "../components/PersonnelPayroll";
import PersonnelOzluk from "../components/personnel/PersonnelOzluk";
import PersonnelLeave from "../components/personnel/PersonnelLeave";
import PersonnelAssets from "../components/PersonnelAssets";
import PersonnelToolbar from "../components/personnel/PersonnelToolbar";
import PersonnelTable from "../components/personnel/PersonnelTable";
import PersonnelCardHeader from "../components/personnel/PersonnelCardHeader";
import RequiredSummary from "../components/personnel/RequiredSummary";
import PersonnelSicilFields from "../components/personnel/PersonnelSicilFields";
import { P_COLS, empty, Row, tl, formFromPerson, personnelBody, missingRequired } from "../components/personnel/personnelShared";

const TABS = (heldCount) => [["sicil", "Sicil / SGK Bilgileri"], ["ucret", "Ücret"], ["izin", "Yıllık İzin"], ["zimmet", `Zimmet Bilgileri${heldCount ? ` (${heldCount})` : ""}`], ["gorev", "Görevlendirme Kayıtları"], ["ozluk", "Özlük Dosyası"], ["bordro", "Bordro"]];

// işten çıkış girilen personelde zimmetli demirbaş uyarısı
function ZimmetWarning({ heldAssets, onGo }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap rounded-md border border-red-300 bg-red-50 px-4 py-2.5" data-testid="pf-zimmet-warning">
      <div className="text-xs text-red-800"><span className="font-bold uppercase">Dikkat:</span> İşten çıkışı girilen bu personelin üzerinde <b>{heldAssets.length}</b> zimmetli demirbaş var ({heldAssets.map((a) => a.name).slice(0, 3).join(", ")}{heldAssets.length > 3 ? "…" : ""}). Demirbaşları teslim alıp zimmeti kaldırın.</div>
      <button type="button" onClick={onGo} data-testid="pf-zimmet-warning-go" className="px-3 py-1 rounded-md bg-red-700 text-white text-xs font-semibold hover:bg-red-800">Zimmet Bilgilerine Git</button>
    </div>
  );
}

// kayıtlı karttaki zimmetli demirbaşlar (sekme değişince tazelenir)
function useHeldAssets(editing, cardTab) {
  const [heldAssets, setHeldAssets] = useState([]);
  useEffect(() => {
    if (!editing || editing === "new") { setHeldAssets([]); return; }
    http.get(`/fixed-assets?assignee_personnel_id=${editing}`).then((r) => setHeldAssets(r.data)).catch(() => setHeldAssets([]));
  }, [editing, cardTab]);
  return heldAssets;
}

export default function Personnel() {
  const { isAdmin, user } = useAuth();
  const sgkAllowed = canSgk(user);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [regions, setRegions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [fProject, setFProject] = useState("");
  const [saving, setSaving] = useState(false);
  const [cardTab, setCardTab] = useState("sicil");
  useNavDraft("personel", () => (editing ? { editing, form, cardTab, q, fProject, soft: editing === "new" && JSON.stringify(form) === JSON.stringify(empty) } : null), (d) => { setEditing(d.editing); setForm(d.form); if (d.cardTab) setCardTab(d.cardTab); if (d.q) setQ(d.q); if (d.fProject) setFProject(d.fProject); });
  const [exportOpen, setExportOpen] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [sel, setSel] = useState([]);
  const formAnchorRef = useRef(null);
  useEffect(() => { if (editing) setTimeout(() => formAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }, [editing]);

  const load = () => http.get("/personnel").then((r) => setItems(r.data)).catch(toastApiError);
  useEffect(() => { load(); http.get("/projects").then((r) => setProjects(r.data)); http.get("/regions").then((r) => setRegions(r.data)); http.get("/locations").then((r) => setLocations(r.data)); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLocaleUpperCase("tr");
    return items.filter((p) => (!fProject || p.project_id === fProject) && (!s || `${p.full_name} ${p.tc_no} ${p.title} ${p.duty || ""} ${p.project_name} ${p.location_name} ${p.phone}`.toLocaleUpperCase("tr").includes(s)));
  }, [items, q, fProject]);
  const [phf, setPhf, visibleRows, pAutoOpts] = useColFilters(filtered, P_COLS);

  const [viewOnly, setViewOnly] = useState(false);
  const heldAssets = useHeldAssets(editing, cardTab);
  const startNew = () => { setCardTab("sicil"); setViewOnly(false); setForm(empty); setEditing("new"); };
  const startEdit = (p) => { setCardTab("sicil"); setViewOnly(false); setForm(formFromPerson(p)); setEditing(p.id); };
  const openCard = (p) => { startEdit(p); setViewOnly(true); };
  const [returnId, setReturnId] = useState(null);
  const [docsKey, setDocsKey] = useState(0);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [backTo, setBackTo] = useState(null);
  // kapatınca: geldiği ekrana dön ya da listedeki satıra kaydırıp vurgula
  const cancel = useCallback(() => {
    const id = editing; setEditing(null); setForm(empty);
    if (backTo) { navigate(backTo); return; }
    if (!id) return;
    setReturnId(id);
    setTimeout(() => { const row = document.querySelector(`[data-testid="personnel-row-${id}"]`); row?.scrollIntoView({ behavior: "smooth", block: "center" }); row?.querySelector(`[data-testid="personnel-open-${id}"]`)?.focus({ preventScroll: true }); }, 50);
    setTimeout(() => setReturnId((r) => (r === id ? null : r)), 2500);
  }, [editing, backTo, navigate]);
  const openedRef = useRef(null);
  // ?yeni=1 / ?personel=<id> derin bağlantıları yalnız liste yüklenince işlenir; params/openCard ref'ten okunur (kapatınca yeniden açılmaz)
  const deepRef = useRef({});
  deepRef.current = { params, openCard };
  useEffect(() => {
    const { params: p, openCard: open } = deepRef.current;
    // Puantaj ekranından "Personel Ekle": yeni kart, TC/proje/pozisyon önceden dolu; kaydet veya ESC → back adresine dön
    if (p.get("yeni") === "1" && openedRef.current !== "yeni") {
      openedRef.current = "yeni";
      setBackTo(safePath(p.get("back")));
      setViewOnly(false);
      setForm({ ...empty, tc_no: (p.get("tc") || "").replace(/\D/g, "").slice(0, 11), project_id: p.get("proje") || "", position: p.get("pozisyon") || "Asıl" });
      setEditing("new");
      return;
    }
    const pid = p.get("personel");
    if (!pid || items.length === 0 || openedRef.current === pid) return;
    const person = items.find((x) => x.id === pid);
    if (!person) return;
    openedRef.current = pid;
    setBackTo(safePath(p.get("back")));
    open(person);
  }, [items]);
  useEffect(() => { if (!editing) return; const k = (e) => { if (e.key === "Escape" && !exportOpen && !document.querySelector("[data-esc-inner]")) { e.stopImmediatePropagation(); cancel(); } }; window.addEventListener("keydown", k, true); return () => window.removeEventListener("keydown", k, true); }, [editing, exportOpen, cancel]); // data-esc-inner: kart içindeki açık alt form ESC'i kendisi tüketir

  const save = async () => {
    const first_name = (form.first_name || "").trim(), last_name = (form.last_name || "").trim();
    const miss = missingRequired(form);
    if (miss.length) { setCardTab("sicil"); return toast.error(`${miss[0].label} zorunlu${miss.length > 1 ? ` (+${miss.length - 1} eksik alan daha)` : ""}`, { field: miss[0].testid }); }
    const body = personnelBody(form, first_name, last_name);
    setSaving(true);
    try {
      let savedId = editing;
      if (editing === "new") { const { data } = await http.post("/personnel", body); savedId = data.id; writeJson("ts-new-ids", [data.id, ...readJson("ts-new-ids", [])].slice(0, 50)); }
      else {
        let ack = false;
        if ((body.leave_date || body.status === "Pasif") && heldAssets.length > 0) {
          ack = window.confirm(`DİKKAT: Bu personelin üzerinde ${heldAssets.length} zimmetli demirbaş var:\n${heldAssets.map((a) => `• ${a.name}${a.serial_no ? ` (${a.serial_no})` : ""}`).join("\n")}\n\nZimmetler kaldırılmadan işten çıkış kaydedilsin mi?`);
          if (!ack) { setSaving(false); setCardTab("zimmet"); return; }
        }
        await http.put(`/personnel/${editing}${ack ? "?ack_zimmet=true" : ""}`, body);
      }
      toast.success("Personel kartı kaydedildi", { link: recordLink(savedId) }); cancel(); load();
    } catch (e) { toastApiError(e); }
    setSaving(false);
  };
  const remove = async (p) => {
    if (!window.confirm(`${p.full_name} kartı silinsin mi? (Çöp kutusuna taşınır)`)) return;
    try { await http.delete(`/personnel/${p.id}`); toast.success("Silindi"); load(); } catch (e) { toastApiError(e); }
  };
  // Excel yükleme: dosya seçilince önce önizleme paneli açılır (kontrol → düzelt → Kaydet)
  const upload = (file) => { setEditing(null); setBulkFile(file); };
  const closeBulk = useCallback(() => setBulkFile(null), []);
  const wageTypes = useMemo(() => [...new Set([...DEFAULT_WAGE_TYPES, ...items.flatMap((p) => (p.wage_history || []).map((w) => w.wage_type || "MAAŞ"))])], [items]);
  // ücret geçmişi değişince listedeki kaydı ve formdaki brüt/net alanlarını güncelle
  const onWageChanged = (p) => { setItems((xs) => xs.map((x) => (x.id === p.id ? p : x))); setForm((f) => ({ ...f, gross_salary: tl(p.gross_salary), net_salary: tl(p.net_salary) })); };
  const current = items.find((p) => p.id === editing);
  const isSicil = cardTab === "sicil";
  const personId = editing !== "new" ? editing : null;

  return (
    <div className="space-y-4" data-testid="personnel-page">
      <PersonnelToolbar q={q} setQ={setQ} fProject={fProject} setFProject={setFProject} projects={projects} count={visibleRows.length} sel={sel} setSel={setSel} items={items} isAdmin={isAdmin} upload={upload} onExport={() => setExportOpen(true)} onBulkRaise={() => setRaiseOpen(true)} onWageChanges={() => setChangesOpen(true)} startNew={startNew} />
      <PersonnelExportDialog open={exportOpen} onClose={() => setExportOpen(false)} projectId={fProject} projects={projects} />
      <WageChangeReportDialog open={changesOpen} onClose={() => setChangesOpen(false)} projects={projects} projectId={fProject} types={wageTypes} />
      {bulkFile && <PersonnelBulkUpload key={bulkFile.name + bulkFile.lastModified} file={bulkFile} projects={projects} regions={regions} locations={locations} onClose={closeBulk} onSaved={load} />}
      <BulkRaiseDialog open={raiseOpen} onClose={() => setRaiseOpen(false)} items={items} projects={projects} regions={regions} locations={locations} onDone={load} />

      <PersonnelTable rows={visibleRows} allCount={filtered.length} hf={phf} setHf={setPhf} autoOpts={pAutoOpts} sel={sel} setSel={setSel} editing={editing} returnId={returnId} isAdmin={isAdmin} openCard={openCard} startEdit={startEdit} remove={remove} />

      <div ref={formAnchorRef} />
      {editing && (
        <div className="rounded-lg border-2 border-orange-300 bg-orange-50/40 p-4 space-y-4" data-testid="personnel-form" onKeyDown={formKeyNavWith(() => { if (!viewOnly && !saving) save(); })}>
          <PersonnelCardHeader editing={editing} form={form} setForm={setForm} load={load} viewOnly={viewOnly} setViewOnly={setViewOnly} onClose={cancel} />
          {!viewOnly && isSicil && <RequiredSummary form={form} />}

          <div className="flex items-center gap-1 border-b border-slate-200" data-testid="pf-tabs">
            {TABS(heldAssets.length).map(([k, l]) => <button key={k} type="button" onClick={() => setCardTab(k)} data-testid={`pf-tab-${k}`} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors ${cardTab === k ? "border-orange-600 text-orange-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>)}
          </div>
          {(form.leave_date || form.status === "Pasif") && heldAssets.length > 0 && cardTab !== "zimmet" && <ZimmetWarning heldAssets={heldAssets} onGo={() => setCardTab("zimmet")} />}
          {cardTab === "ucret" && <PersonnelWage form={form} setForm={setForm} person={current} viewOnly={viewOnly} isAdmin={isAdmin} onChanged={onWageChanged} typeSuggestions={[...new Set(items.flatMap((p) => (p.wage_history || []).map((w) => w.wage_type || "MAAŞ")))]} />}
          {cardTab === "bordro" && <PersonnelPayroll person={current} />}
          {cardTab === "ozluk" && <PersonnelOzluk personId={personId} personName={form.full_name} personTc={form.tc_no} />}
          {cardTab === "izin" && <PersonnelLeave person={current} onDocsChanged={() => setDocsKey((k) => k + 1)} />}
          {cardTab === "zimmet" && <PersonnelAssets personId={personId} personName={form.full_name} />}
          {cardTab === "gorev" && <TempAssignmentHistory personId={personId} workAreas={current?.work_areas} />}
          {isSicil && <PersonnelSicilFields form={form} setForm={setForm} editing={editing} viewOnly={viewOnly} projects={projects} regions={regions} locations={locations} docsKey={docsKey} dutyOptions={[...new Set(items.map((p) => p.duty).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"))} />}
          {isSicil && <div className="xl:w-1/2 xl:ml-auto xl:pl-4"><Row label="">{sgkAllowed
            ? <SgkLinks form={form} personId={personId} location={locations.find((l) => l.id === form.location_id)} transferTo={locations.find((l) => l.id === form.transfer_to_location_id)} onDocumentSaved={() => setDocsKey((k) => k + 1)} />
            : <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="sgk-no-permission"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><b>SGK e-İşe Giriş / e-İşten Çıkış</b> yalnız Yönetici ve bu yetki verilmiş İK kullanıcıları tarafından yapılabilir. Yetki için yöneticinize başvurun (Kullanıcılar → Yetkiler → İşlem Yetkileri).</span></div>}</Row></div>}
          {isSicil && editing !== "new" && <PersonnelDocuments personId={editing} refreshKey={docsKey} />}
          <div className="flex gap-2 items-center">
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={cancel} data-testid="pf-cancel" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-semibold"><X className="w-3.5 h-3.5" /> İptal</button>
              {!viewOnly && <button type="button" onClick={save} disabled={saving} data-testid="pf-save" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-60"><Save className="w-3.5 h-3.5" /> {saving ? "Kaydediliyor…" : "Kaydet"}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
